import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Layout } from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  FileText, 
  Check, 
  Clock, 
  AlertTriangle,
  Loader2,
  CreditCard,
  Download,
  Banknote,
  Calendar,
  Receipt,
  Users,
  Undo2,
  History,
  Wallet,
  X
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function MyInvoicesPage() {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [invoices, setInvoices] = useState([]);
  const [eventPayments, setEventPayments] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [downloading, setDownloading] = useState(null);
  const [paying, setPaying] = useState(null);
  const [checkingPayment, setCheckingPayment] = useState(false);
  const [paymentInfo, setPaymentInfo] = useState({ iban: '', bank_name: '', club_name: '' });
  const [activeTab, setActiveTab] = useState('all'); // 'all', 'invoices', 'events', 'transactions'

  const getToken = () => localStorage.getItem('shrt_token');

  useEffect(() => {
    fetchPayments();
    fetchPaymentInfo();
  }, []);

  const fetchPaymentInfo = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/payment-info`);
      setPaymentInfo(response.data);
    } catch (error) {
      console.error('Error fetching payment info:', error);
    }
  };

  // Check for payment success/cancel from URL params
  useEffect(() => {
    const paymentStatus = searchParams.get('payment');
    const sessionId = searchParams.get('session_id');
    const invoiceId = searchParams.get('invoice_id');
    
    if (paymentStatus === 'success' && invoiceId) {
      if (sessionId) {
        checkPaymentStatus(invoiceId, sessionId);
      } else {
        // Payment Link flow (no session_id) — verify via Stripe API
        verifyPaymentLinkPayment(invoiceId);
      }
    } else if (paymentStatus === 'cancelled') {
      toast.info('Zahlung abgebrochen');
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [searchParams]);

  const verifyPaymentLinkPayment = async (invoiceId) => {
    setCheckingPayment(true);
    
    try {
      const response = await axios.post(
        `${API_URL}/api/payments/invoice/${invoiceId}/verify-payment`
      );
      
      if (response.data.status === 'paid' || response.data.status === 'already_paid') {
        toast.success('Zahlung erfolgreich! Vielen Dank.');
        fetchPayments();
      } else if (response.data.status === 'not_paid') {
        toast.info('Zahlung wird verarbeitet... Bitte aktualisiere die Seite in ein paar Minuten.');
      }
    } catch (error) {
      console.error('Payment verification failed:', error);
    } finally {
      setCheckingPayment(false);
      window.history.replaceState({}, '', window.location.pathname);
    }
  };

  const checkPaymentStatus = async (invoiceId, sessionId) => {
    setCheckingPayment(true);
    const token = getToken();
    
    try {
      const response = await axios.get(
        `${API_URL}/api/payments/invoice/${invoiceId}/status?session_id=${sessionId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.payment_status === 'paid') {
        toast.success('Zahlung erfolgreich! Vielen Dank.');
        fetchPayments(); // Refresh to show updated status
      } else {
        toast.info('Zahlung wird verarbeitet...');
      }
    } catch (error) {
      console.error('Payment status check failed:', error);
    } finally {
      setCheckingPayment(false);
      // Clean URL
      window.history.replaceState({}, '', window.location.pathname);
    }
  };

  const handlePayment = async (invoice) => {
    setPaying(invoice.id);
    const token = getToken();
    
    try {
      const response = await axios.post(
        `${API_URL}/api/payments/invoice/checkout`,
        {
          invoice_id: invoice.id,
          origin_url: window.location.origin
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.checkout_url) {
        // Redirect to Stripe Checkout
        window.location.href = response.data.checkout_url;
      } else {
        toast.error('Zahlungslink konnte nicht erstellt werden');
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Fehler beim Starten der Zahlung';
      toast.error(message);
    } finally {
      setPaying(null);
    }
  };

  const fetchPayments = async () => {
    setLoading(true);
    const token = getToken();
    
    try {
      const response = await axios.get(
        `${API_URL}/api/profile/payments`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setInvoices(response.data.invoices || []);
      setEventPayments(response.data.event_payments || []);
      setTransactions(response.data.transactions || []);
    } catch (error) {
      toast.error('Fehler beim Laden der Zahlungen');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadInvoice = async (invoiceId, invoiceNumber) => {
    setDownloading(`invoice-${invoiceId}`);
    const token = getToken();
    
    try {
      const response = await axios.get(
        `${API_URL}/api/profile/invoices/${invoiceId}/pdf`,
        { 
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Rechnung_${invoiceNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Rechnung heruntergeladen');
    } catch (error) {
      toast.error('Fehler beim Herunterladen');
    } finally {
      setDownloading(null);
    }
  };

  const handleDownloadReceipt = async (type, id, name) => {
    setDownloading(`receipt-${type}-${id}`);
    const token = getToken();
    
    const endpoint = type === 'invoice' 
      ? `${API_URL}/api/profile/invoices/${id}/receipt`
      : `${API_URL}/api/profile/events/${id}/receipt`;
    
    try {
      const response = await axios.get(endpoint, { 
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Quittung_${name}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Quittung heruntergeladen');
    } catch (error) {
      toast.error('Fehler beim Herunterladen der Quittung');
    } finally {
      setDownloading(null);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('de-CH');
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('de-CH', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('de-CH', {
      style: 'currency',
      currency: 'CHF'
    }).format(amount || 0);
  };

  // Filter items based on active tab
  const filteredInvoices = activeTab === 'events' || activeTab === 'transactions' ? [] : invoices;
  const filteredEvents = activeTab === 'invoices' || activeTab === 'transactions' ? [] : eventPayments;

  // Stats
  const paidInvoices = invoices.filter(i => i.status === 'paid');
  const openInvoices = invoices.filter(i => i.status === 'open');
  const refundedInvoices = invoices.filter(i => i.status === 'refunded');
  const totalPaid = paidInvoices.length + eventPayments.length;

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center gap-3">
          <CreditCard className="text-primary" size={28} />
          <div>
            <h1 className="font-['Oswald'] text-2xl font-bold text-foreground uppercase tracking-tight">
              Meine Rechnungen
            </h1>
            <p className="text-sm text-muted-foreground">
              Übersicht Ihrer Mitgliedsbeiträge und Event-Zahlungen
            </p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 border-b border-border overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          <button
            onClick={() => setActiveTab('all')}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
              activeTab === 'all' 
                ? 'border-primary text-primary' 
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            Alle ({invoices.length + eventPayments.length})
          </button>
          <button
            onClick={() => setActiveTab('invoices')}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
              activeTab === 'invoices' 
                ? 'border-primary text-primary' 
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            Mitgliedsbeiträge ({invoices.length})
          </button>
          <button
            onClick={() => setActiveTab('events')}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
              activeTab === 'events' 
                ? 'border-primary text-primary' 
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            Events ({eventPayments.length})
          </button>
          <button
            onClick={() => setActiveTab('transactions')}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap flex items-center gap-1.5 ${
              activeTab === 'transactions' 
                ? 'border-primary text-primary' 
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            <History size={14} />
            Transaktionen ({transactions.length})
          </button>
        </div>

        {/* Transactions List */}
        {activeTab === 'transactions' && (
          <div className="bg-card border border-border">
            {loading ? (
              <div className="flex items-center justify-center h-32">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            ) : transactions.length === 0 ? (
              <div className="p-8 text-center text-muted-foreground">
                <History className="mx-auto mb-4 h-12 w-12 opacity-50" />
                <p>Keine Transaktionen vorhanden</p>
              </div>
            ) : (
              <div className="divide-y divide-border">
                {transactions.map((tx) => (
                  <div key={tx.id} className="p-4 hover:bg-muted/20">
                    <div className="flex items-center justify-between gap-4">
                      {/* Left: Icon and Details */}
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-full flex-shrink-0 ${
                          tx.type === 'refund' 
                            ? 'bg-purple-500/20 text-purple-500'
                            : tx.is_stripe 
                              ? 'bg-blue-500/20 text-blue-500'
                              : 'bg-green-500/20 text-green-500'
                        }`}>
                          {tx.type === 'refund' ? (
                            <Undo2 size={18} />
                          ) : tx.is_stripe ? (
                            <CreditCard size={18} />
                          ) : (
                            <Wallet size={18} />
                          )}
                        </div>
                        <div>
                          <p className="font-medium text-foreground text-sm">
                            {tx.description}
                          </p>
                          <div className="flex items-center gap-2 text-xs text-muted-foreground">
                            <span>{formatDateTime(tx.date)}</span>
                            <span>•</span>
                            <span>{tx.method}</span>
                          </div>
                          {tx.reason && (
                            <p className="text-xs text-muted-foreground mt-1">
                              Grund: {tx.reason}
                            </p>
                          )}
                        </div>
                      </div>
                      
                      {/* Right: Amount */}
                      <div className={`text-right font-bold ${
                        tx.type === 'refund' ? 'text-purple-500' : 'text-green-500'
                      }`}>
                        {tx.type === 'refund' ? '' : '+'}{formatCurrency(tx.amount)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Invoice List */}
        {activeTab !== 'transactions' && (
        <div className="bg-card border border-border">
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : (filteredInvoices.length === 0 && filteredEvents.length === 0) ? (
            <div className="p-8 text-center text-muted-foreground">
              <FileText className="mx-auto mb-4 h-12 w-12 opacity-50" />
              <p>Keine {activeTab === 'events' ? 'Event-Zahlungen' : activeTab === 'invoices' ? 'Rechnungen' : 'Zahlungen'} vorhanden</p>
            </div>
          ) : (
            <div className="divide-y divide-border">
              {/* Membership Invoices */}
              {filteredInvoices.map((invoice) => {
                const isPaid = invoice.status === 'paid';
                const isRefunded = invoice.status === 'refunded';
                const isCancelled = invoice.status === 'cancelled';
                const isOverdue = !isPaid && !isRefunded && !isCancelled && invoice.due_date && new Date(invoice.due_date) < new Date();
                
                return (
                  <div key={`invoice-${invoice.id}`} className="p-4 md:p-6 hover:bg-muted/20">
                    {/* Mobile-first layout */}
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                      {/* Left: Status Icon and Invoice Info */}
                      <div className="flex items-start sm:items-center gap-3 sm:gap-4">
                        {/* Status Icon */}
                        <div className={`p-2 sm:p-3 rounded-full flex-shrink-0 ${
                          isCancelled
                            ? 'bg-gray-500/20 text-gray-500'
                            : isRefunded
                            ? 'bg-purple-500/20 text-purple-500'
                            : isPaid 
                              ? 'bg-green-500/20 text-green-500' 
                              : isOverdue 
                                ? 'bg-red-500/20 text-red-500'
                                : 'bg-amber-500/20 text-amber-500'
                        }`}>
                          {isCancelled ? (
                            <X size={20} className="sm:w-6 sm:h-6" />
                          ) : isRefunded ? (
                            <Undo2 size={20} className="sm:w-6 sm:h-6" />
                          ) : isPaid ? (
                            <Check size={20} className="sm:w-6 sm:h-6" />
                          ) : isOverdue ? (
                            <AlertTriangle size={20} className="sm:w-6 sm:h-6" />
                          ) : (
                            <Clock size={20} className="sm:w-6 sm:h-6" />
                          )}
                        </div>
                        
                        {/* Invoice Info */}
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="font-['Oswald'] text-base sm:text-lg font-bold text-foreground">
                              Mitgliedsbeitrag {invoice.year}
                            </h3>
                            <span className="text-xs px-2 py-0.5 bg-muted text-muted-foreground">
                              BEITRAG
                            </span>
                          </div>
                          <p className="text-xs sm:text-sm text-muted-foreground">
                            {invoice.invoice_number}
                          </p>
                        </div>
                      </div>
                      
                      {/* Right: Amount, Status and Actions */}
                      <div className="flex items-center justify-between sm:justify-end gap-3 sm:gap-4 ml-11 sm:ml-0">
                        <div className="text-left sm:text-right">
                          <p className="text-lg sm:text-xl font-bold text-foreground">
                            {formatCurrency(invoice.total_amount || invoice.amount)}
                          </p>
                          <p className={`text-xs sm:text-sm font-medium ${
                            isCancelled
                              ? 'text-gray-500'
                              : isRefunded
                              ? 'text-purple-500'
                              : isPaid 
                                ? 'text-green-500' 
                                : isOverdue 
                                  ? 'text-red-500'
                                  : 'text-amber-500'
                          }`}>
                            {isCancelled ? (
                              <span className="flex items-center gap-1 line-through">
                                Storniert
                              </span>
                            ) : isRefunded ? (
                              <span className="flex items-center gap-1">
                                <Undo2 size={12} className="sm:w-3.5 sm:h-3.5" />
                                Rückerstattet
                              </span>
                            ) : isPaid ? (
                              <span className="flex items-center gap-1">
                                <Check size={12} className="sm:w-3.5 sm:h-3.5" />
                                Bezahlt
                              </span>
                            ) : isOverdue ? (
                              <span className="flex items-center gap-1">
                                <AlertTriangle size={12} className="sm:w-3.5 sm:h-3.5" />
                                <span className="hidden sm:inline">Überfällig</span>
                                <span className="sm:hidden">Überfällig</span>
                              </span>
                            ) : (
                              <span className="flex items-center gap-1">
                                <Clock size={12} className="sm:w-3.5 sm:h-3.5" />
                                <span className="hidden sm:inline">Fällig {formatDate(invoice.due_date)}</span>
                                <span className="sm:hidden">Offen</span>
                              </span>
                            )}
                          </p>
                        </div>
                        
                        {/* Action Buttons */}
                        <div className="flex gap-2">
                          {isPaid && (
                            <Button
                              variant="outline"
                              size="default"
                              onClick={() => handleDownloadReceipt('invoice', invoice.id, invoice.invoice_number)}
                              disabled={downloading === `receipt-invoice-${invoice.id}`}
                              className="rounded-none flex-shrink-0"
                              data-testid={`receipt-invoice-${invoice.id}`}
                            >
                              {downloading === `receipt-invoice-${invoice.id}` ? (
                                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                              ) : (
                                <Receipt className="h-4 w-4 mr-2" />
                              )}
                              Quittung
                            </Button>
                          )}
                          <Button
                            variant="outline"
                            size="default"
                            onClick={() => handleDownloadInvoice(invoice.id, invoice.invoice_number)}
                            disabled={downloading === `invoice-${invoice.id}`}
                            className="rounded-none flex-shrink-0"
                          >
                            {downloading === `invoice-${invoice.id}` ? (
                              <Loader2 className="h-4 w-4 animate-spin mr-2" />
                            ) : (
                              <Download className="h-4 w-4 mr-2" />
                            )}
                            Rechnung
                          </Button>
                        </div>
                      </div>
                    </div>
                    
                    {/* Payment Instructions for open invoices */}
                    {!isPaid && !isRefunded && (
                      <div className="mt-4 p-4 sm:p-6 bg-muted/30 border border-border ml-11 sm:ml-0">
                        {/* Online Payment Button */}
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-5">
                          <div>
                            <p className="text-base font-medium text-foreground mb-1">
                              Jetzt online bezahlen
                            </p>
                            <p className="text-sm text-muted-foreground">
                              Sicher online bezahlen
                            </p>
                          </div>
                          <Button
                            onClick={() => handlePayment(invoice)}
                            disabled={paying === invoice.id || checkingPayment}
                            size="lg"
                            className="bg-primary hover:bg-primary/90 text-primary-foreground rounded-none uppercase tracking-wider"
                            data-testid={`pay-invoice-${invoice.id}`}
                          >
                            {paying === invoice.id ? (
                              <>
                                <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                                Wird geladen...
                              </>
                            ) : (
                              <>
                                <Banknote className="h-5 w-5 mr-2" />
                                Jetzt bezahlen
                              </>
                            )}
                          </Button>
                        </div>
                        
                        <div className="border-t border-border pt-4">
                          <p className="text-sm font-medium text-foreground mb-3">
                            Alternativ per Banküberweisung:
                          </p>
                          <div className="space-y-2 text-sm">
                            {paymentInfo.bank_name && (
                              <p className="text-foreground">
                                <span className="text-muted-foreground">Bank:</span> {paymentInfo.bank_name}
                              </p>
                            )}
                            {paymentInfo.iban && (
                              <p className="text-foreground font-mono">
                                <span className="text-muted-foreground font-sans">IBAN:</span> {paymentInfo.iban}
                              </p>
                            )}
                            <p className="text-foreground">
                              <span className="text-muted-foreground">Verwendungszweck:</span> <strong>{invoice.invoice_number}</strong>
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
              
              {/* Event Payments */}
              {filteredEvents.map((payment) => (
                <div key={`event-${payment.id}`} className="p-4 md:p-6 hover:bg-muted/20">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    {/* Left: Icon and Event Info */}
                    <div className="flex items-start sm:items-center gap-3 sm:gap-4">
                      {/* Status Icon - always green for paid events */}
                      <div className="p-2 sm:p-3 rounded-full flex-shrink-0 bg-green-500/20 text-green-500">
                        <Calendar size={20} className="sm:w-6 sm:h-6" />
                      </div>
                      
                      {/* Event Info */}
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="font-['Oswald'] text-base sm:text-lg font-bold text-foreground">
                            {payment.event_title}
                          </h3>
                          <span className="text-xs px-2 py-0.5 bg-blue-500/20 text-blue-500">
                            EVENT
                          </span>
                        </div>
                        <div className="flex items-center gap-3 text-xs sm:text-sm text-muted-foreground">
                          <span>{payment.event_date}</span>
                          {payment.guests > 0 && (
                            <span className="flex items-center gap-1">
                              <Users size={12} />
                              {1 + payment.guests} Personen
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    {/* Right: Amount, Status and Actions */}
                    <div className="flex items-center justify-between sm:justify-end gap-3 sm:gap-4 ml-11 sm:ml-0">
                      <div className="text-left sm:text-right">
                        <p className="text-lg sm:text-xl font-bold text-foreground">
                          {formatCurrency(payment.amount)}
                        </p>
                        <p className="text-xs sm:text-sm font-medium text-green-500 flex items-center gap-1">
                          <Check size={12} className="sm:w-3.5 sm:h-3.5" />
                          Bezahlt
                        </p>
                      </div>
                      
                      {/* Receipt Download Button */}
                      <Button
                        variant="outline"
                        size="default"
                        onClick={() => handleDownloadReceipt('event', payment.id, payment.event_title)}
                        disabled={downloading === `receipt-event-${payment.id}`}
                        className="rounded-none flex-shrink-0"
                        data-testid={`receipt-event-${payment.id}`}
                      >
                        {downloading === `receipt-event-${payment.id}` ? (
                          <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        ) : (
                          <Receipt className="h-4 w-4 mr-2" />
                        )}
                        Quittung
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        )}

        {/* Summary */}
        {(invoices.length > 0 || eventPayments.length > 0) && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-green-500/10 border border-green-500/30 p-4 text-center">
              <p className="text-2xl font-bold text-green-600">
                {totalPaid}
              </p>
              <p className="text-xs text-muted-foreground uppercase">Bezahlt</p>
            </div>
            <div className="bg-card border border-border p-4 text-center">
              <p className="text-2xl font-bold text-amber-500">
                {openInvoices.length}
              </p>
              <p className="text-xs text-muted-foreground uppercase">Offen</p>
            </div>
            <div className="bg-purple-500/10 border border-purple-500/30 p-4 text-center">
              <p className="text-2xl font-bold text-purple-600">
                {refundedInvoices.length}
              </p>
              <p className="text-xs text-muted-foreground uppercase">Rückerstattet</p>
            </div>
            <div className="bg-card border border-border p-4 text-center">
              <p className="text-2xl font-bold text-foreground">
                {formatCurrency(
                  openInvoices.reduce((sum, i) => sum + (i.total_amount || i.amount || 0), 0)
                )}
              </p>
              <p className="text-xs text-muted-foreground uppercase">Ausstehend</p>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
